import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const cases = [
  {
    industry: "Home Services",
    icon: "ri-home-gear-line",
    problem: "Missed 30–40% of inbound calls",
    solution: "AI call handling + booking system",
    metrics: [{ value: "+55%", label: "Booked Jobs" }, { value: "<60s", label: "Response Time" }],
    img: "https://public.readdy.ai/ai/img_res/f77274b1e8b55d9a91193d48d65926a7.jpg",
  },
  {
    industry: "Law Firm",
    icon: "ri-scales-line",
    problem: "Manual intake causing delays and lost clients",
    solution: "Automated intake + qualification funnel",
    metrics: [{ value: "+40%", label: "Consultation Bookings" }, { value: "50%", label: "Less Admin Work" }],
    img: "https://readdy.ai/api/search-image?query=law%20firm%20attorney%20professional%20office%20modern%20clean%20minimal%20elegant%20neutral%20tones%20premium%20photography%20success%20results&width=700&height=420&seq=cs-legal-002&orientation=landscape",
  },
  {
    industry: "Med Spa",
    icon: "ri-heart-pulse-line",
    problem: "Leads not converting after initial inquiry",
    solution: "Automated follow-up + nurture system",
    metrics: [{ value: "+35%", label: "Booked Treatments" }, { value: "Higher", label: "Retention Rate" }],
    img: "https://readdy.ai/api/search-image?query=luxury%20med%20spa%20wellness%20clinic%20modern%20interior%20clean%20white%20aesthetic%20professional%20premium%20photography%20calm%20serene%20results&width=700&height=420&seq=cs-medspa-003&orientation=landscape",
  },
  {
    industry: "Insurance Agency",
    icon: "ri-shield-line",
    problem: "Leads not followed up consistently",
    solution: "Multi-step nurture + pipeline automation",
    metrics: [{ value: "Higher", label: "Conversion Rate" }, { value: "Shorter", label: "Sales Cycle" }],
    img: "https://readdy.ai/api/search-image?query=insurance%20business%20professional%20office%20modern%20clean%20corporate%20neutral%20tones%20success%20growth%20premium%20photography&width=700&height=420&seq=cs-insurance-004&orientation=landscape",
  },
  {
    industry: "Healthcare Practice",
    icon: "ri-hospital-line",
    problem: "Missed appointments and scheduling gaps",
    solution: "Automated reminders + intake system",
    metrics: [{ value: "Fewer", label: "No-Shows" }, { value: "Better", label: "Operational Flow" }],
    img: "https://readdy.ai/api/search-image?query=modern%20healthcare%20clinic%20medical%20practice%20clean%20white%20interior%20professional%20staff%20happy%20patients%20premium%20photography%20bright&width=700&height=420&seq=cs-health-005&orientation=landscape",
  },
];

export default function CaseStudies() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#0a0a0a]" : "bg-white"}`}>
      <Navbar />
      <main>
        <section className="pt-40 pb-20 text-center px-6">
          <p className={`text-[11px] tracking-[0.15em] uppercase font-semibold mb-6 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Proof of Work</p>
          <h1 className={`text-5xl md:text-7xl font-black leading-tight tracking-[-0.03em] mb-4 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>Real systems.</h1>
          <h1 className={`text-5xl md:text-7xl font-black leading-tight tracking-[-0.03em] mb-8 ${isDark ? "text-white/30" : "text-[#6e6e73]"}`}>Real outcomes.</h1>
          <p className={`text-lg max-w-xl mx-auto font-light ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>Every result below came from a fully deployed AI system — not a tool, not a plugin. A complete solution.</p>
        </section>

        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-28 flex flex-col gap-5">
          {cases.map((c, idx) => (
            <div key={c.industry} className={`rounded-2xl overflow-hidden border transition-colors duration-300 ${isDark ? "bg-[#111111] border-white/6" : "bg-[#f5f5f7] border-black/5"}`}>
              <div className="w-full h-52 overflow-hidden">
                <img src={c.img} alt={c.industry} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/8" : "bg-white"}`}>
                    <i className={`${c.icon} text-lg ${isDark ? "text-white/70" : "text-[#1d1d1f]/70"}`}></i>
                  </div>
                  <span className={`text-xs tracking-widest uppercase font-semibold ${isDark ? "text-white/45" : "text-[#6e6e73]"}`}>{c.industry}</span>
                  <span className={`ml-auto text-sm font-black ${isDark ? "text-white/15" : "text-[#1d1d1f]/15"}`}>0{idx + 1}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="border-l-2 border-red-400/50 pl-4">
                    <p className={`text-[10px] tracking-widest uppercase mb-2 ${isDark ? "text-white/30" : "text-[#6e6e73]"}`}>Problem</p>
                    <p className={`text-sm leading-relaxed ${isDark ? "text-white/70" : "text-[#1d1d1f]/80"}`}>{c.problem}</p>
                  </div>
                  <div className={`border-l-2 pl-4 ${isDark ? "border-white/15" : "border-black/12"}`}>
                    <p className={`text-[10px] tracking-widest uppercase mb-2 ${isDark ? "text-white/30" : "text-[#6e6e73]"}`}>Solution</p>
                    <p className={`text-sm leading-relaxed ${isDark ? "text-white/70" : "text-[#1d1d1f]/80"}`}>{c.solution}</p>
                  </div>
                  <div className="border-l-2 border-emerald-500/50 pl-4">
                    <p className={`text-[10px] tracking-widest uppercase mb-2 ${isDark ? "text-white/30" : "text-[#6e6e73]"}`}>Result</p>
                    <div className="flex gap-6">
                      {c.metrics.map((m) => (
                        <div key={m.label}>
                          <p className={`font-black text-2xl tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{m.value}</p>
                          <p className={`text-xs ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className={`py-24 text-center px-6 ${isDark ? "bg-[#111111]" : "bg-[#f5f5f7]"}`}>
          <h2 className={`text-3xl md:text-5xl font-black mb-6 tracking-[-0.02em] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>Want results like these?</h2>
          <p className={`text-lg mb-10 max-w-xl mx-auto font-light ${isDark ? "text-white/50" : "text-[#6e6e73]"}`}>Let&apos;s audit your current process and show you exactly where revenue is being lost.</p>
          <Link to="/contact" className={`inline-block px-8 py-4 font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${isDark ? "bg-white text-[#0a0a0a] hover:bg-white/90" : "bg-[#1d1d1f] text-white hover:bg-[#1d1d1f]/85"}`}>
            Book Your AI Strategy Call
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
